import { useEffect } from 'react';
import useAuthStore from '../../stores/authStore';
import useNotificationsStore from '../../stores/notificationsStore';
import { USER_ROLE } from '../../types/auth';
import StaffInfoRequestModal from './StaffInfoRequestModal';

/**
 * Auto-opens the personal-info modal for staff with a pending request (AC2).
 *
 * Mounted once in AppLayout so it covers every staff page, not just the
 * dashboard. Renders nothing for other roles.
 *
 * The prompt is deferred for the session once dismissed, so it won't reappear
 * while navigating — but it does return on the next login, which is the point:
 * this is a blocking payroll requirement, not a suggestion.
 */
const StaffInfoRequestGate = () => {
	const role = useAuthStore(state => state.user?.role);
	const isStaff = role === USER_ROLE.STAFF;

	const infoRequestState = useNotificationsStore(state => state.infoRequestState);
	const businessName = useNotificationsStore(state => state.infoRequest?.businessName ?? null);
	const isDismissed = useNotificationsStore(state => state.isInfoRequestDismissed);
	const isOpen = useNotificationsStore(state => state.isInfoRequestModalOpen);
	const openModal = useNotificationsStore(state => state.openInfoRequestModal);
	const dismiss = useNotificationsStore(state => state.dismissInfoRequest);
	const markCompleted = useNotificationsStore(state => state.markInfoRequestCompleted);

	useEffect(() => {
		if (isStaff && infoRequestState === 'PENDING' && !isDismissed) {
			openModal();
		}
	}, [isStaff, infoRequestState, isDismissed, openModal]);

	// Expired requests aren't staff-actionable — the provider has to resend, so
	// there's nothing to prompt for here. The bell still reports the state.
	if (!isStaff || infoRequestState !== 'PENDING') return null;

	return (
		<StaffInfoRequestModal
			isOpen={isOpen}
			onRemindLater={dismiss}
			onCompleted={markCompleted}
			businessName={businessName}
		/>
	);
};

export default StaffInfoRequestGate;
